import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLinkClickHandler } from 'react-router-dom';
import {
  ColumnMappingConfig,
  RelatedDocument,
} from '@asset-metrix/portal-gateway-client-js';
import {
  getActiveContentGroupTabUrlSearchKey,
  RelatedDocuments,
} from '@asset-metrix/portal-ui';
import { useQuery } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import {
  DatatableRelatedDocumentsQueryKey,
  fetchDatatableRelatedDocuments,
} from '../../../api/portalGateway/datatableRelatedDocuments';
import {
  NO_ROLE_ID,
  portalGatewayClient,
} from '../../../api/portalGatewayClient';
import { createTableFilterSearchParamKey } from '../../../hooks/usePresetTableFiltersFromUrl';
import { useStore } from '../../../store';
import { prependRoleId } from '../../../utils/prependRoleId';
import { triggerDownloadWithAnchorTag } from '../../../utils/triggerDownloadWithAnchorTag';
import { pageWithRoleLink } from '../../../utils/urlPath';
import { mapColumnData } from '../mapColumnData';

import { TableSidebarProps } from './useTableSidebarProperties';

export type RelatedDocumentsLoaderProps = Omit<TableSidebarProps, 'onClose'> & {
  widgetId: string;
};

export const RelatedDocumentsLoader = ({
  data,
  relatedDocumentsConfig,
  widgetId,
  opened,
}: RelatedDocumentsLoaderProps) => {
  const { t } = useTranslation();
  const [selectedDocument, setSelectedDocument] = useState<
    RelatedDocument | undefined
  >(undefined);
  const [selectedDocumentPreviewUrl, setSelectedDocumentPreviewUrl] = useState<
    string | undefined
  >(undefined);

  const { roleId, setFilterInitialised } = useStore(
    useShallow((state) => ({
      roleId: state.activeUserRole?.id,
      setFilterInitialised: state.setFilterInitialised,
    }))
  );

  const selection = useMemo(
    () => mapColumnData(relatedDocumentsConfig?.columnMapping, data?.data),
    [relatedDocumentsConfig?.columnMapping, data?.data]
  );

  const linkedPageSearchParams = useMemo(() => {
    const searchParams = new URLSearchParams();
    if (relatedDocumentsConfig?.linkedPage?.filteredWidget) {
      const mappings = relatedDocumentsConfig.columnMapping;
      for (const k in mappings) {
        const { id, key, display } = mappings[k];
        const searchKey = createTableFilterSearchParamKey(
          relatedDocumentsConfig.linkedPage.filteredWidget.id,
          id
        );
        const value = selection[key];
        searchParams.set(searchKey, mapToFilterValue(display, value));
      }
    }

    if (relatedDocumentsConfig?.linkedPage?.filteredWidget?.tab) {
      const searchKey = getActiveContentGroupTabUrlSearchKey(
        relatedDocumentsConfig?.linkedPage?.filteredWidget?.tab.group
      );
      const tabValue =
        relatedDocumentsConfig?.linkedPage?.filteredWidget?.tab.value;

      searchParams.set(searchKey, tabValue);
    }
    const searchParamsString = searchParams.toString();
    return searchParamsString ? `?${searchParams.toString()}` : '';
  }, [
    relatedDocumentsConfig?.columnMapping,
    relatedDocumentsConfig?.linkedPage?.filteredWidget,
    selection,
  ]);

  const href = pageWithRoleLink(roleId ?? NO_ROLE_ID, {
    slug: relatedDocumentsConfig?.linkedPage?.completeSlug,
    search: linkedPageSearchParams,
    pageType: 'page',
  });

  const handleClick = useLinkClickHandler<HTMLButtonElement>(href ?? '');
  const onOpenInPage = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      // Mark the filter as uninitialized, so any filters that have been set before will be overwritten
      // by the url parameters.
      const widgetId = relatedDocumentsConfig?.linkedPage?.filteredWidget?.id;
      if (widgetId) {
        setFilterInitialised(widgetId, false);
      }
      handleClick(event);
    },
    [handleClick, relatedDocumentsConfig, setFilterInitialised]
  );

  const {
    data: relatedDocumentsData,
    isFetching,
    error,
  } = useQuery({
    queryKey: prependRoleId<DatatableRelatedDocumentsQueryKey>([
      'datatableRelatedDocuments',
      widgetId,
      selection,
    ]),
    queryFn: fetchDatatableRelatedDocuments,
    enabled: opened,
  });

  // Reset selected document and preview URL when sidebar content changes
  useEffect(() => {
    setSelectedDocument(undefined);
    setSelectedDocumentPreviewUrl(undefined);
  }, [selection]);

  const onDocumentDownload = async () => {
    try {
      if (!selectedDocument) {
        return;
      }
      const { data } =
        await portalGatewayClient.table.getRelatedDocumentDownloadUrl(
          widgetId,
          selectedDocument.id,
          { selection }
        );

      if (data?.status === 'success' && data.data.url) {
        triggerDownloadWithAnchorTag(data.data.url);
      }
    } catch (e: unknown) {
      console.error(e);
      throw e;
    }
  };

  const onDocumentClick = async (document: RelatedDocument) => {
    if (document.id === selectedDocument?.id) {
      setSelectedDocument(undefined);
      setSelectedDocumentPreviewUrl(undefined);
      return;
    }

    setSelectedDocument(document);
    try {
      // only show preview for pdf documents
      if (document.mimeType !== 'application/pdf') {
        setSelectedDocumentPreviewUrl(undefined);
        return;
      }

      const { data } =
        await portalGatewayClient.table.getRelatedDocumentDownloadUrl(
          widgetId,
          document.id,
          { selection }
        );

      if (data?.status === 'success' && data.data.url) {
        setSelectedDocumentPreviewUrl(data.data.url);
      } else {
        setSelectedDocumentPreviewUrl(undefined);
      }
    } catch (e: unknown) {
      console.error(e);
    }
  };

  return (
    <RelatedDocuments
      error={error}
      documents={relatedDocumentsData?.data.items}
      loading={isFetching}
      onDocumentDownload={onDocumentDownload}
      onOpenInPage={
        relatedDocumentsConfig?.linkedPage ? onOpenInPage : undefined
      }
      openInPageButtonText={
        relatedDocumentsConfig?.linkedPage?.buttonText ??
        t('app.tableSidebarWidget.openInPage')
      }
      selectedDocument={selectedDocument}
      onDocumentClick={onDocumentClick}
      selectedDocumentPreviewUrl={selectedDocumentPreviewUrl}
    />
  );
};

function mapToFilterValue(
  display: ColumnMappingConfig['display'],
  value: string
) {
  switch (display) {
    case 'range-date-picker':
      return JSON.stringify({
        gte: value.slice(0, 10),
        lte: value.slice(0, 10),
      });
    default:
      return value;
  }
}
